// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { InternalLink } from '@/components/link';
import { Ticket, TicketCategory } from '@/ticket/api/types';
import { categories } from '@/ticket/util/category';
import { formatNumber } from '@/utilities/number';
import { Box, TableCell, TableRow, Typography } from '@mui/material';

interface TicketTableRowProps {
  ticket: Ticket;
  isLastRow: boolean;
}

export function TicketTableRow({ ticket, isLastRow }: TicketTableRowProps) {
  const renderCategoryLabel = (category: TicketCategory) => {
    return categories[category]?.label;
  };

  const renderTooltipContent = (
    items: { serial?: string; name?: string }[],
    pluralType: string
  ) => {
    if (items.length === 0) {
      return;
    }
    const maxItems = 5;
    const displayItems = items.slice(0, maxItems);
    const remainingCount = items.length - maxItems;

    return (
      <Box sx={{ p: 1 }}>
        {displayItems.map((item, index) => (
          <Typography key={index} variant="body2">
            {item.serial || item.name}
          </Typography>
        ))}
        {remainingCount > 0 && (
          <Typography variant="body2">
            ... and {remainingCount} other{' '}
            {remainingCount > 1
              ? pluralType
              : pluralType.substring(0, pluralType.length - 1)}
          </Typography>
        )}
      </Box>
    );
  };
  const ticketKey = ticket.key;
  return (
    <TableRow
      hover
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        borderBottom: isLastRow ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      <TableCell>
        <InternalLink
          to={`/tickets/$ticketKey`}
          params={{ ticketKey }}
          color="primary"
          sx={{
            fontWeight: 'medium',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {ticket.title}
        </InternalLink>
      </TableCell>
      <TableCell>{renderCategoryLabel(ticket.category)}</TableCell>
      <TableCell>
        {ticket.assignedTo
          ? ticket.assignedTo.firstName + ' ' + ticket.assignedTo.lastName
          : 'Unassigned'}
      </TableCell>
      <TableCell>
        {ticket.createdAt?.toLocaleString({
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            cursor: 'pointer',
            textDecoration: ticket.assets.length > 0 ? 'underline' : 'none',
            textDecorationStyle: 'dotted',
          }}
        >
          <ConditionalTooltip
            tooltipText={renderTooltipContent(ticket.assets, 'assets')}
            arrow
          >
            {formatNumber(ticket.assets.length)}
          </ConditionalTooltip>
        </Box>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            cursor: 'pointer',
            textDecoration: ticket.projects.length > 0 ? 'underline' : 'none',
            textDecorationStyle: 'dotted',
          }}
        >
          <ConditionalTooltip
            tooltipText={renderTooltipContent(ticket.projects, 'projects')}
            arrow
          >
            {formatNumber(ticket.projects.length)}
          </ConditionalTooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}
